/**
 * The long moves' motion kit (ท่ายาว: ท่าใหญ่ set pieces and lock routines, design docs/design/combat/2026-10-11-long-moves.md
 * §4.3): each art's file (motions/long/<art>.ts) writes its beats as keys here and gets back its key poses and motions,
 * blended on the Catmull-Rom spline (motions/spline.ts) like every other motion. Same key layout as the bout's qinggong
 * (motions/duelMoves.ts): every key is laid over the standing pose as a stance key (`IDLE_KEYS`), which gives the rig a
 * weight over one leg, a turn of the shoulders and its own feet, and places both hands.
 *
 * Beside the pose, a key can carry what the rig cannot pose (`lift` off the ground, `rot` of the whole body, `turn` of
 * the view in 45° heading steps), blended with the same eases and handed to the renderer with the frame
 * (`LongFrame`, the shape `received` in renderer.ts draws).
 */
import type { Stance } from '../../rig/skeleton';
import { IDLE_KEYS } from '../idles';
import { eased, type Ease, type FarArm, type NearArm, type Rig } from '../kit';

/** One key of a long-move motion. */
export interface LongKey {
  /** The weight: +1 all over the right leg (A, the weapon side), −1 the left (B); its size is the hips' shift. */
  w?: number;
  /** Feet apart (as `Bearing.stance`). */
  feet?: number;
  /** Forward offset of the back (B) and front (A) foot. */
  f: [number, number];
  /** Lift of the back (B) and front (A) foot. */
  l?: [number, number];
  o: number;
  lean?: number;
  tilt: number;
  /** The shoulders turned about the spine (positive: the right shoulder forward). */
  turn?: number;
  hipDrop?: number;
  shoulderDrop?: number;
  sway?: number;
  hem?: number;
  flare?: number;
  headDx?: number;
  hunch?: number;
  arm: NearArm;
  far: FarArm;
  /** The weapon hand's own place (classic design coordinates, as `POSE_HANDS`), over its arm shape's. */
  A?: [number, number];
  /** The off hand's own place. */
  B?: [number, number];
  face?: Rig['face'];
  closed?: boolean;
  /** Drawn beside the pose: art px off the ground (`liftH` scales by the beat's height), body roll (rad, + = pitched
   * forward), view turn (45° steps). */
  lift?: number;
  rot?: number;
  view?: number;
}

/** A motion as an art writes it: its keys, what it starts from (a key pose of another motion, or `ready`), the eases. */
export interface LongMotionSpec {
  keys: LongKey[];
  /** A pose the motion starts on (the previous beat's last key), left out: starts on its own first key. */
  from?: string;
  /** A pose it ends on (`ready`), left out: ends on its own last key. */
  to?: string;
  /** One per step (from → first key … last key → to); `inOut` where none is given. */
  ease?: Ease[];
}

/** What the renderer draws for a long-move body this frame (renderer.ts `received`). */
export interface LongFrame {
  id: string;
  /** 0–1 through the motion. */
  u: number;
  /** Heading steps (45°) the view is turned by, relative to the heading held at the beat's start. */
  turn: number;
  /** Turn about the feet (radians, the figure's own frame: negative = over backwards). */
  rot: number;
  /** Art pixels off the ground. */
  lift: number;
}

/** Per-key channels of a motion (one value per key of the motion, its `from`/`to` included). */
export interface LongChannels {
  lift: number[];
  rot: number[];
  turn: number[];
}

/**
 * Builds an art's key poses (`<motion>_<i>`, laid over the standing pose through `IDLE_KEYS`) and motions, and the
 * channels beside them. `T` is the art's motion ids, `P` the union of its pose names.
 */
export function buildLong<T extends string, P extends string>(
  label: string,
  specs: Record<T, LongMotionSpec>,
): {
  POSES: Record<P, () => Partial<Rig>>;
  MOTIONS: { id: T; keys: readonly string[]; ease: readonly Ease[] }[];
  CHANNELS: Record<T, LongChannels>;
} {
  const poses: Record<string, () => Partial<Rig>> = {};
  const motions: { id: T; keys: readonly string[]; ease: readonly Ease[] }[] = [];
  const channels = {} as Record<T, LongChannels>;
  const keyByName = new Map<string, LongKey>();
  for (const [id, spec] of Object.entries(specs) as [T, LongMotionSpec][]) {
    const names: string[] = [];
    spec.keys.forEach((k, i) => {
      const name = `${id}_${i + 1}`;
      names.push(name);
      keyByName.set(name, k);
      const a = Math.abs(k.w ?? 0);
      const stance = {
        label,
        female: false,
        keys: [name, name],
        motion: id,
        feet: k.feet ?? 1.2,
        ...(a > 0.02 ? { weight: (k.w ?? 0) > 0 ? ('A' as const) : ('B' as const) } : {}),
        shift: a,
        shiftIn: 0,
        hipDrop: k.hipDrop ?? 0,
        shoulderDrop: k.shoulderDrop ?? 0,
        step: 0,
        turn: k.turn ?? 0,
        tilt: k.tilt,
        breath: 0,
        tuck: 1,
        rest: 'side',
      } as unknown as Stance;
      const hands = k.A || k.B ? { hands: { ...(k.A ? { A: k.A } : {}), ...(k.B ? { B: k.B } : {}) } } : {};
      IDLE_KEYS.set(name, { stance, inhale: true, feet: k.f, ...hands });
      const rig: Partial<Rig> = {
        o: k.o,
        lean: k.lean ?? 0,
        tilt: k.tilt,
        headDx: k.headDx ?? 0,
        l1: k.l?.[0] ?? 0,
        l2: k.l?.[1] ?? 0,
        sway: k.sway ?? 0,
        hem: k.hem ?? 0,
        flare: k.flare ?? 0,
        arm: k.arm,
        far: k.far,
        face: k.face ?? 'atk',
        closed: k.closed ?? false,
        ...(k.hunch ? { hunch: k.hunch } : {}),
      };
      poses[name] = () => rig;
    });
    const keys = [...(spec.from ? [spec.from] : []), ...names, ...(spec.to ? [spec.to] : [])];
    const ease = Array.from({ length: keys.length - 1 }, (_, i) => spec.ease?.[i] ?? ('inOut' as const));
    motions.push({ id, keys, ease });
    const ch = (f: (k: LongKey | undefined) => number) => keys.map((n) => f(keyByName.get(n)));
    // A `from` key of another motion carries that key's channels; `ready` and plain poses none.
    channels[id] = {
      lift: ch((k) => k?.lift ?? 0),
      rot: ch((k) => k?.rot ?? 0),
      turn: ch((k) => k?.view ?? 0),
    };
  }
  return { POSES: poses as Record<P, () => Partial<Rig>>, MOTIONS: motions, CHANNELS: channels };
}

/** A channel's value `u` (0–1) through a motion, blended between its keys with the motion's own eases. */
export function channelAt(vals: readonly number[], ease: readonly Ease[], u: number): number {
  const n = vals.length;
  if (n < 2) return vals[0] ?? 0;
  const x = Math.max(0, Math.min(1, u)) * (n - 1);
  const i = Math.min(n - 2, Math.floor(x));
  const t = eased(ease[i] ?? 'inOut', x - i);
  return vals[i]! + (vals[i + 1]! - vals[i]!) * t;
}
